import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import WorkflowShowcase from "./WorkflowShowcase";
import {
  buildAppLink,
  redirectForAttachment,
  redirectForChat,
  redirectForFeature,
  redirectForModelSelect,
  redirectForVoiceInput,
} from "../../../utils/landingRedirect";
import type { FeatureAction } from "../../../utils/landingRedirect";

const heroEntryCards: Array<{
  key: string;
  icon?: string;
  imageSrc?: string;
  color: string;
  action: FeatureAction;
  subtitleNoWrap?: boolean;
}> = [
  { key: "record", icon: "ion:mic-outline", color: "#8B5CF6", action: "realtime_recording" },
  { key: "upload", icon: "ion:cloud-upload-outline", color: "#4ea4f5", action: "attach_file", subtitleNoWrap: true },
  { key: "agent", imageSrc: "/logo/logo.png", color: "#4ea4f5", action: "chat", subtitleNoWrap: true },
  { key: "create", icon: "ion:videocam-outline", color: "#10B981", action: "post_recording" },
  { key: "learn", icon: "ion:book-outline", color: "#FFA500", action: "generate_quiz" },
];

const InteractiveHero: React.FC = () => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const promptFallback = String(t("modern.interactiveHero.hero.promptFallback"));
  const promptPlaceholder = String(t("modern.interactiveHero.hero.promptPlaceholder"));
  const promptLabel = String(t("modern.interactiveHero.hero.promptLabel"));
  const attachLabel = String(t("modern.interactiveHero.hero.attachLabel"));
  const voiceLabel = String(t("modern.interactiveHero.hero.voiceLabel"));
  const sendLabel = String(t("modern.interactiveHero.hero.sendLabel"));

  const handlePromptSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    redirectForChat(prompt.trim() || promptFallback);
  };

  return (
    <div className="w-full bg-white relative overflow-hidden">
      {/* 1. HERO SECTION (Title, Text, Buttons) */}
      <section className="relative z-10 flex min-h-screen items-center pb-14 pt-24 md:pb-20 md:pt-24">
        <div className="mx-auto w-full px-4 md:px-[38px]">
          <div className="flex flex-col items-center text-center max-w-[1376px] mx-auto">
            {/* Main Heading */}
            <motion.h1
              className="font-bold mb-5"
              style={{ fontSize: "clamp(27px, 6vw, 52px)" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <span className="text-gray-900">{t("modern.interactiveHero.hero.title")}</span>{" "}
              <span className="gradient-text">
                {t("modern.interactiveHero.hero.titleHighlight")}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="mb-6 mx-auto max-w-3xl"
              style={{ fontSize: "20px", color: "#726F6F" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {t("modern.interactiveHero.hero.subtitle")}
            </motion.p>

            <motion.div
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="mx-auto mb-5 grid w-full max-w-[930px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 lg:gap-3.5">
                {heroEntryCards.map((card) => {
                  const isAgent = card.key === "agent";
                  return (
                    <motion.button
                      key={card.key}
                      onClick={() => redirectForFeature(card.action)}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      className={`flex h-[116px] cursor-pointer flex-col items-center justify-center rounded-[17px] border bg-white px-3 py-2.5 text-center transition-all lg:h-[126px] lg:rounded-[18px] lg:px-3 lg:py-3 ${
                        isAgent
                          ? "border-[#a8d2ff] shadow-[0_10px_30px_rgba(78,164,245,0.12)]"
                          : "border-[#c9cbd1] hover:border-[#a8d2ff]"
                      }`}
                    >
                      {card.imageSrc ? (
                        <img
                          alt=""
                          aria-hidden="true"
                          className="mb-1.5 h-[28px] w-[28px] object-contain lg:mb-2 lg:h-[32px] lg:w-[32px]"
                          src={card.imageSrc}
                        />
                      ) : card.icon ? (
                        <Icon icon={card.icon} className="mb-1.5 h-[28px] w-[28px] lg:mb-2 lg:h-[34px] lg:w-[34px]" style={{ color: card.color }} />
                      ) : null}
                      <span className="text-[20px] font-bold leading-tight text-[#111111] lg:text-[21px]">
                        {t(`modern.interactiveHero.hero.entryCards.${card.key}.title`)}
                      </span>
                      <span
                        className={`mt-1 max-w-full text-[13.5px] leading-[1.16] text-[#8e8e8e] lg:text-[14px] lg:leading-[1.12] ${
                          card.subtitleNoWrap ? "whitespace-nowrap" : "whitespace-pre-line"
                        }`}
                      >
                        {t(`modern.interactiveHero.hero.entryCards.${card.key}.subtitle`)}
                      </span>
                    </motion.button>
                  );
                })}
              </div>

              <form
                className="mx-auto flex min-h-[76px] w-full max-w-[930px] flex-col justify-between rounded-[20px] border border-[#c9cbd1] bg-white px-4 py-2.5 text-left shadow-[0_8px_20px_rgba(17,24,39,0.035)] lg:min-h-[86px] lg:rounded-[24px] lg:px-5 lg:py-3"
                onSubmit={handlePromptSubmit}
              >
                <textarea
                  aria-label={promptLabel}
                  className="block min-h-[24px] w-full resize-none bg-transparent p-0 font-inter text-[17px] leading-tight text-gray-900 placeholder:text-[#909090] focus:outline-none lg:text-[21px]"
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={promptPlaceholder}
                  rows={1}
                  value={prompt}
                />
                <div className="flex items-center gap-3">
                  <button
                    aria-label={attachLabel}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent text-[#111111] hover:bg-gray-100 lg:h-9 lg:w-9"
                    onClick={redirectForAttachment}
                    type="button"
                  >
                    <Icon icon="lucide:plus" className="text-[22px] lg:text-[26px]" />
                  </button>
                  <button
                    className="flex cursor-pointer items-center gap-2 bg-transparent text-[16px] font-medium text-[#111111] hover:text-cornflowerblue lg:text-[20px]"
                    onClick={redirectForModelSelect}
                    type="button"
                  >
                    {t("modern.interactiveHero.hero.aiMode")}
                    <Icon icon="lucide:chevron-down" className="text-[18px] text-[#555555] lg:text-[22px]" />
                  </button>
                  <div className="flex-1" />
                  <button
                    aria-label={voiceLabel}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent text-[#111111] hover:bg-gray-100 lg:h-9 lg:w-9"
                    onClick={redirectForVoiceInput}
                    type="button"
                  >
                    <Icon icon="ion:mic-outline" className="text-[22px] lg:text-[27px]" />
                  </button>
                  <button
                    aria-label={sendLabel}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-[#e5e7eb] bg-white text-[#bfc2c8] hover:border-cornflowerblue hover:bg-cornflowerblue hover:text-white lg:h-10 lg:w-10"
                    type="submit"
                  >
                    <Icon icon="lucide:arrow-up" className="text-[20px] lg:text-[23px]" />
                  </button>
                </div>
              </form>

              <motion.div
                className="mx-auto mt-7 grid w-full max-w-[780px] grid-cols-1 gap-3 sm:grid-cols-3 lg:mt-8"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <a
                    href={buildAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 w-full items-center justify-center gap-2.5 rounded-[22px] border border-[#cad7e6] bg-white px-4 text-[15px] font-black text-[#34527a] no-underline shadow-[0_8px_18px_rgba(78,164,245,0.06),inset_0_0_0_1px_#e4ebf3] transition-colors hover:border-[#bcd5f2] hover:bg-[#f7fbff] lg:h-[50px] lg:rounded-[25px] lg:text-[17px]"
                  >
                    <Icon icon="mdi:gesture-tap-button" className="text-[21px] text-black lg:text-[24px]" />
                    {t("modern.interactiveHero.hero.startUsing")}
                  </a>
                </motion.div>
                <motion.div className="flex-1" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <a
                    href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 w-full items-center justify-center gap-2.5 rounded-[22px] border border-[#cad7e6] bg-white px-4 text-[15px] font-black text-[#34527a] no-underline shadow-[0_8px_18px_rgba(78,164,245,0.06),inset_0_0_0_1px_#e4ebf3] transition-colors hover:border-[#bcd5f2] hover:bg-[#f7fbff] lg:h-[50px] lg:rounded-[25px] lg:text-[17px]"
                  >
                    <Icon icon="lucide:apple" className="text-[20px] text-black lg:text-[23px]" />
                    {t("modern.interactiveHero.hero.downloadIos")}
                  </a>
                </motion.div>

                <motion.div className="flex-1" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <a
                    href="https://downloads.youwo.ai/mac/YouWo-latest.dmg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 w-full items-center justify-center gap-2.5 rounded-[22px] border border-[#cad7e6] bg-white px-4 text-[15px] font-black text-[#34527a] no-underline shadow-[0_8px_18px_rgba(78,164,245,0.06),inset_0_0_0_1px_#e4ebf3] transition-colors hover:border-[#bcd5f2] hover:bg-[#f7fbff] lg:h-[50px] lg:rounded-[25px] lg:text-[17px]"
                  >
                    <Icon icon="lucide:monitor-down" className="text-[20px] text-black lg:text-[23px]" />
                    {t("modern.interactiveHero.hero.downloadMac")}
                  </a>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <WorkflowShowcase />
    </div>
  );
};

export default InteractiveHero;
